import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useI18nSafe } from '../context/I18nContext';
import { hasStoredAdminSession } from '../lib/adminClientAuth';

function normalizeMaintenanceState(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (typeof payload.maintenanceActive !== 'undefined') {
    return Boolean(payload.maintenanceActive);
  }
  return Boolean(payload.maintenanceMode);
}

export default function MaintenancePreviewBanner() {
  const { t } = useI18nSafe();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadPreviewState() {
      try {
        const settingsPromise = fetch('/api/site-settings', {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const adminPromise = hasStoredAdminSession()
          ? fetch('/api/admin/profile', { credentials: 'same-origin', cache: 'no-store' })
          : null;

        const settingsResponse = await settingsPromise;
        const adminResponse = adminPromise ? await adminPromise : null;

        if (!isMounted) return;

        if (!settingsResponse.ok || (adminResponse && !adminResponse.ok)) {
          setIsVisible(false);
          return;
        }

        const settings = await settingsResponse.json().catch(() => ({}));
        setIsVisible(normalizeMaintenanceState(settings));
      } catch (err) {
        if (isMounted) {
          setIsVisible(false);
        }
      }
    }

    loadPreviewState();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!isVisible) return null;

  return (
    <section className="border-b border-amber-400/30 bg-[linear-gradient(90deg,rgba(120,53,15,0.95),rgba(146,64,14,0.92),rgba(67,20,7,0.96))] text-amber-50 shadow-[0_10px_30px_rgba(0,0,0,0.2)]">
      <div className="container mx-auto flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-full border border-amber-200/30 bg-white/10 p-2">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-amber-100/90">
              {t('maintenance.preview.eyebrow', 'Admin preview')}
            </p>
            <p className="mt-1 text-sm font-semibold text-white">
              {t(
                'maintenance.preview.title',
                'Maintenance mode is active for visitors. You are previewing the public website with admin access.',
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            href="/admin/dashboard"
            className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/15"
          >
            {t('maintenance.preview.dashboard', 'Back to dashboard')}
          </Link>
          <Link
            href="/maintenance"
            className="rounded-full border border-amber-100/25 px-4 py-2 text-xs font-semibold text-amber-50/90 transition hover:bg-white/10"
          >
            {t('maintenance.preview.visitorView', 'Open visitor maintenance page')}
          </Link>
        </div>
      </div>
    </section>
  );
}
